import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import apiClient from '../apiClient';
import { useAuth } from '../contexts/AuthContext';

export default function SignIn() {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Check for session expiration and restore email
  const sessionExpired = localStorage.getItem('sessionExpired') === 'true';
  const lastUserEmail = localStorage.getItem('lastUserEmail') || '';
  
  const [form, setForm] = useState({
    email: lastUserEmail,
    password: ''
  });
  const [error, setError] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initializationProgress, setInitializationProgress] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // const [resendMessage, setResendMessage] = useState(''); // TODO: Implement resend verification
  
  // Set session expired message on mount
  React.useEffect(() => {
    if (sessionExpired) {
      setSessionMessage('Your session has expired. Please sign in again.');
      localStorage.removeItem('sessionExpired');
      // Clear the message after the user starts typing
      const timer = setTimeout(() => {
        localStorage.removeItem('lastUserEmail');
      }, 300000); // Clear after 5 minutes
      return () => clearTimeout(timer);
    }
  }, [sessionExpired]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear session message when user starts typing
    if (sessionMessage) {
      setSessionMessage('');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setIsInitializing(false);
    setInitializationProgress('');

    try {
      const result = await login(form.email, form.password);
      
      if (result.success) {
        const user = JSON.parse(localStorage.getItem('ezocmUser'));
        console.log('User after login:', user);
        
        if (user.role === 'Team Member') {
          navigate('/my-assessments');
        } else if (user.role === 'Change Manager' || user.role === 'Admin') {
          navigate('/reports');
        } else {
          console.error('Unknown role:', user.role);
          setError('Invalid user role. Please contact support.');
        }
      } else {
        // Handle different types of errors
        if (result.isInitializing) {
          setIsInitializing(true);
          setInitializationProgress(`Initializing system... (attempt ${result.retryAttempt}/${result.maxRetries})`);
          setError(result.error);
        } else if (result.isColdStart) {
          setIsInitializing(true);
          setInitializationProgress('System is warming up, please wait...');
          setError(result.error);
        } else {
          // Provide clearer error messages
          if (result.error?.toLowerCase().includes('invalid credentials') || 
              result.error?.toLowerCase().includes('unauthorized') ||
              result.error?.toLowerCase().includes('invalid email or password')) {
            setError('Invalid email or password. Please check your credentials and try again.');
          } else if (result.error?.toLowerCase().includes('not found')) {
            setError('No account found with this email address. Please check your email or create a new account.');
          } else if (result.error?.toLowerCase().includes('locked') || result.error?.toLowerCase().includes('disabled')) {
            setError('Your account has been locked. Please contact support for assistance.');
          } else {
            setError(result.error || 'Failed to sign in. Please try again.');
          }
        }
      }
    } catch (err) {
      console.error('Sign in error:', err.response?.data || err);
      
      // Check if this looks like a cold start issue
      const isColdStartError = 
        err.code === 'ECONNABORTED' ||
        err.response?.status === 503 ||
        err.response?.status === 502 ||
        err.response?.status === 504 ||
        err.message?.includes('timeout') ||
        err.message?.includes('Network Error');
      
      if (isColdStartError) {
        setIsInitializing(true);
        setInitializationProgress('System is starting up...');
        setError('The system is initializing. This typically takes 30-60 seconds on first access.');
      } else {
        setError(err.response?.data?.error || 'Failed to sign in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotPasswordMessage('');
    setIsLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email: form.email });
      setForgotPasswordMessage('Password reset instructions have been sent to your email.');
    } catch (err) {
      setError('Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative">
        {/* Close Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close sign in form"
        >
          <IoClose size={24} />
        </button>

        <h2 className="text-3xl font-bold text-blue-900 mb-6">
          {showForgotPassword ? 'Reset Password' : 'Sign In'}
        </h2>
        
        {sessionMessage && (
          <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
            {sessionMessage}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            {typeof error === 'object' && error.error ? (
              <>
                <h4 className="text-red-800 font-semibold mb-1">{error.error}</h4>
                {error.message && <p className="text-red-700 text-sm mb-2">{error.message}</p>}
                {error.action && (
                  <p className="text-red-700 text-sm mb-2">
                    <strong>What to do:</strong> {error.action}
                  </p>
                )}
                {error.help && (
                  <p className="text-red-600 text-xs italic">{error.help}</p>
                )}
              </>
            ) : (
              <p className="text-red-700 text-sm">{error}</p>
            )}
          </div>
        )}

        {forgotPasswordMessage && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
            {forgotPasswordMessage}
          </div>
        )}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-700 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(false)}
                className="text-sm text-blue-700 hover:text-blue-800 font-medium"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-700 hover:text-blue-800 font-medium"
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-700 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                isInitializing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {initializationProgress || 'Initializing system...'}
                  </div>
                ) : (
                  'Signing In...'
                )
              ) : (
                'Sign In'
              )}
            </button>

            <div className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-blue-700 hover:text-blue-800 font-medium"
              >
                Create one
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
} 
